// Supplies the login footer with the operating organization's details.
//
// Why this exists: the organization name and address used to be two literals in the `auth` message
// catalog. That made the front end a second source of truth for who operates the service, and it is why
// the footer and the rest of the product disagreed for months after the operating organization changed
// (SHCT-624). They now come from the backend, which reads the one `institution` database row, so an
// organization change is a database update with no rebuild and no redeploy.

import { useEffect, useState } from 'react';
import { endpoints } from '@/api/endpoints';
import { httpClient } from '@/api/http-client';

/**
 * What the footer renders. Every field is optional: the organization row allows a missing address or
 * representative, and a client must render the absence rather than an empty labelled row.
 *
 * The address arrives WITHOUT a label prefix, and this footer renders it without one — unlike the admin
 * footer, which is exactly why the label is not stored with the value.
 */
export interface PublicInstitution {
  name: string | null;
  address: string | null;
  representative: string | null;
}

const EMPTY: PublicInstitution = { name: null, address: null, representative: null };

interface ApiEnvelope<T> {
  data: T;
}

/**
 * Reads the organization details for the footer.
 *
 * Renders nothing rather than something wrong. While the request is in flight — and if it fails — every
 * field stays null and the footer omits those rows, filling them in once the data arrives. There is
 * deliberately no hardcoded fallback text: a fallback is exactly the stale copy this work removes, and it
 * would silently restore the defect the moment the endpoint broke.
 *
 * A failure is logged rather than surfaced as a toast or modal. This is a knowing departure from the
 * repository's error-handling standard, which classifies an API failure as a System Error that must be
 * shown to the user. The footer is decorative page furniture on a screen whose actual purpose — logging
 * in — is unaffected by its absence, so an error dialog here would interrupt a working login to report
 * that a line of small print is missing. Recorded as a tracked deviation rather than made quietly.
 */
export function useInstitution(): PublicInstitution {
  const [institution, setInstitution] = useState<PublicInstitution>(EMPTY);

  useEffect(() => {
    let active = true;
    httpClient
      .get<ApiEnvelope<PublicInstitution>>(endpoints.institution)
      .then((res) => {
        const data = res.data?.data;
        // Guard against a response arriving after the screen has gone: setting state on an unmounted
        // component is a React warning, and a successful login unmounts this page.
        if (!active || !data || typeof data !== 'object') return;
        setInstitution({
          name: data.name ?? null,
          address: data.address ?? null,
          representative: data.representative ?? null,
        });
      })
      .catch((e: unknown) => {
        console.error('[institution] footer organization details unavailable', e);
      });
    return () => {
      active = false;
    };
  }, []);

  return institution;
}
